import React, { useMemo, useState } from 'react';
import ReForm, { type NestedObject, type JsonFormsData } from '@restore/reform';
import { Box, Container } from '@mui/system';
import { datadogRum } from '@datadog/browser-rum';
import { schema, withSeededSections, withoutEmptySeededSections } from './schema';
import { uischema } from './uischema';
import { getRealmName } from 'src/routes/AuthContext';
import SucessModal from '../sucess-modal';
import CommonLoader from 'src/utils/common-loader';
import { codesApiUrl, codesApiSecurityCode } from 'src/config';
import DocumentService from 'src/services/documentService';

type FormStatus = 'ready' | 'submitting' | 'success' | 'error';

const NO_PREFILL: NestedObject = {};

type ScreeningFormProps = {
  /** Nested initial data keyed by section id (e.g. `{ basicDetails: { patientName } }`). */
  prefill?: NestedObject;
  /** Discipline of the signed-in therapist, passed straight through to Reform. */
  discipline?: string;
  /**
   * SCREENING webhook uuid (minted via pullUuid) — carries the patient/screening context to
   * document-service's POST /document/form/screening/{formId}.
   */
  webhookUuid: string;
  /** The facility's SCREENING template form id (document.formId from get-screening-template). */
  formId: string;
  /** Invoked after the success/error modal is dismissed (navigate back to the list). */
  onDone?: () => void;
};

const ScreeningForm: React.FC<ScreeningFormProps> = ({
  prefill = NO_PREFILL,
  discipline,
  webhookUuid,
  formId,
  onDone,
}) => {
  const [formStatus, setFormStatus] = useState<FormStatus>('ready');
  const [errorMessage, setErrorMessage] = useState<string>('Something went wrong');
  const realm = getRealmName();
  const facilityId = localStorage.getItem('facilityId');
  // Memoised so Reform, which keys an effect on initialData, sees a stable reference.
  const initialData = useMemo(() => withSeededSections(prefill) as NestedObject, [prefill]);

  // The schema is stored locally in provider-portal, so Reform's own submit/token machinery
  // is not used — pass a no-op token getter like the addendum-note form does.
  const getAccessToken = async () => '';

  // JOTFORM-DISCONNECT(BRK-1925): the real native submission. Document-service renders the
  // PDF via Printimus Prime and persists through provider-service — no Jotform anywhere.
  const submitCallback = async (data: JsonFormsData) => {
    setFormStatus('submitting');
    try {
      const { error } = await DocumentService.submitScreening(
        realm ?? '',
        formId,
        webhookUuid,
        facilityId || '',
        withoutEmptySeededSections(data as Record<string, unknown>)
      );
      if (error) {
        datadogRum.addAction('screening_submitForm_ERROR', { realm, facilityId, error });
        setErrorMessage(typeof error === 'string' ? error : 'Something went wrong');
        setFormStatus('error');
        return;
      }
      datadogRum.addAction('screening_submitForm', {
        realm,
        facilityId,
        sections: Object.keys(data || {}),
      });
      setFormStatus('success');
    } catch (error: any) {
      console.error('Error handling screening submission:', error);
      datadogRum.addAction('screening_submitForm_ERROR', { realm, error: error?.message });
      setErrorMessage('An error occurred while submitting the form');
      setFormStatus('error');
    }
  };

  const handleClose = () => {
    if (onDone) onDone();
  };

  let component;
  switch (formStatus) {
    case 'submitting':
      component = <CommonLoader />;
      break;

    case 'success':
      component = (
        <SucessModal
          title="Submitted"
          message="Your screening has been received"
          close={handleClose}
        />
      );
      break;

    case 'error':
      component = (
        <SucessModal
          title="Error"
          message={errorMessage}
          isSucess={false}
          close={handleClose}
        />
      );
      break;

    case 'ready':
    default:
      component = (
        <ReForm
          // schema/uischema are built dynamically from screeningFields, so their inferred TS
          // shape is wider than Reform's JsonSchema/UISchema types — cast at the boundary.
          schema={schema as any}
          uiSchema={uischema as any}
          submitCallback={submitCallback}
          initialData={initialData}
          formUuid=""
          realm=""
          apiUrl=""
          aiApiUrl=""
          codesApiUrl={codesApiUrl}
          codesApiSecurityCode={codesApiSecurityCode}
          getAccessToken={getAccessToken}
          facilityId={facilityId || ''}
          discipline={discipline as any}
        />
      );
      break;
  }

  return (
    <Box
      component="main"
      sx={{ flexGrow: 1 }}
    >
      <Container
        maxWidth={false}
        disableGutters
        sx={{ px: '2.5rem' }}
      >
        {component}
      </Container>
    </Box>
  );
};

export default ScreeningForm;
